import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import settlementReportAPI from "../api/settlementReportAPI";
import "./SettlementDifference.css";

const present = (value) => value !== null && value !== undefined && value !== "";
const todayKey = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};
const dateKey = (value) => {
  if (!present(value)) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

const statusMatches = (status, trackingExists, orderExists, settlementExists) => {
  switch (status) {
    case "TRACKING_ENTRIES": return trackingExists;
    case "ORDER_ENTRIES": return orderExists;
    case "SETTLEMENT_ENTRIES": return settlementExists;
    case "T1_T2_MATCHED": return trackingExists && orderExists;
    case "T1_T2_UNMATCHED": return (trackingExists || orderExists) && !(trackingExists && orderExists);
    case "T2_T3_MATCHED": return orderExists && settlementExists;
    case "T2_T3_UNMATCHED": return (orderExists || settlementExists) && !(orderExists && settlementExists);
    case "FULLY_MATCHED": return trackingExists && orderExists && settlementExists;
    default: return true;
  }
};

export default function SettlementDifference() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => ({ orderId: "", trackingId: "", seller: "", platform: "", status: "ALL", fromDate: todayKey(), toDate: todayKey() }));

  const load = async () => {
    setLoading(true);
    try {
      const data = await settlementReportAPI.getThreeReportDifference();
      setRows(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  const platforms = useMemo(() => [...new Set(rows.map((row) => row.platform).filter(Boolean))], [rows]);

  const dateScopedRows = useMemo(() => rows.filter((row) => {
    const reportDates = [row.fetch_date, row.dispatch_date, row.settlement_dispatch_date].map(dateKey).filter(Boolean);
    if (reportDates.length === 0) return false;
    return reportDates.some((date) => (!filters.fromDate || date >= filters.fromDate) && (!filters.toDate || date <= filters.toDate));
  }), [rows, filters.fromDate, filters.toDate]);

  const summaryCards = useMemo(() => {
    const counts = { TRACKING_ENTRIES: 0, ORDER_ENTRIES: 0, SETTLEMENT_ENTRIES: 0, T1_T2_MATCHED: 0, T1_T2_UNMATCHED: 0, T2_T3_MATCHED: 0, T2_T3_UNMATCHED: 0, FULLY_MATCHED: 0 };
    dateScopedRows.forEach((row) => {
      const trackingExists = present(row.tracking_entry_id);
      const orderExists = present(row.order_entry_id);
      const settlementExists = present(row.settlement_entry_id);
      if (trackingExists) counts.TRACKING_ENTRIES += 1;
      if (orderExists) counts.ORDER_ENTRIES += 1;
      if (settlementExists) counts.SETTLEMENT_ENTRIES += 1;
      if (trackingExists && orderExists) counts.T1_T2_MATCHED += 1;
      else if (trackingExists || orderExists) counts.T1_T2_UNMATCHED += 1;
      if (orderExists && settlementExists) counts.T2_T3_MATCHED += 1;
      else if (orderExists || settlementExists) counts.T2_T3_UNMATCHED += 1;
      if (trackingExists && orderExists && settlementExists) counts.FULLY_MATCHED += 1;
    });
    return [
      { id: "TRACKING_ENTRIES", label: "1. Tracking Report", hint: "Uploaded entries", value: counts.TRACKING_ENTRIES, tone: "blue" },
      { id: "T1_T2_MATCHED", label: "1 to 2 Matched", hint: "Tracking matched with order detail", value: counts.T1_T2_MATCHED, tone: "green" },
      { id: "T1_T2_UNMATCHED", label: "1 to 2 Unmatched", hint: "Missing in either report", value: counts.T1_T2_UNMATCHED, tone: "red" },
      { id: "ORDER_ENTRIES", label: "2. Order Detail Report", hint: "Uploaded entries", value: counts.ORDER_ENTRIES, tone: "blue" },
      { id: "T2_T3_MATCHED", label: "2 to 3 Matched", hint: "Order detail matched with settlement", value: counts.T2_T3_MATCHED, tone: "green" },
      { id: "T2_T3_UNMATCHED", label: "2 to 3 Unmatched", hint: "Missing in either report", value: counts.T2_T3_UNMATCHED, tone: "red" },
      { id: "SETTLEMENT_ENTRIES", label: "3. Settlement Report", hint: "Uploaded entries", value: counts.SETTLEMENT_ENTRIES, tone: "purple" },
      { id: "FULLY_MATCHED", label: "Fully Matched", hint: "Matched in all three reports", value: counts.FULLY_MATCHED, tone: "green" },
    ];
  }, [dateScopedRows]);

  const visible = useMemo(() => dateScopedRows.filter((row) => {
    const trackingExists = present(row.tracking_entry_id);
    const orderExists = present(row.order_entry_id);
    const settlementExists = present(row.settlement_entry_id);
    if (filters.orderId && !String(row.order_id || "").toLowerCase().includes(filters.orderId.toLowerCase())) return false;
    if (filters.trackingId && !String(row.tracking_id || "").toLowerCase().includes(filters.trackingId.toLowerCase())) return false;
    if (filters.seller && !String(row.seller_name || "").toLowerCase().includes(filters.seller.toLowerCase())) return false;
    if (filters.platform && row.platform !== filters.platform) return false;
    return statusMatches(filters.status, trackingExists, orderExists, settlementExists);
  }), [dateScopedRows, filters]);

  const updateFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }));

  return (
    <main className="settlement-difference-page">
      <header><div><span>Settlement Reports</span><h1>Settlement Difference</h1><p>First report to Order Detail to Settlement report matching.</p></div><button type="button" onClick={() => navigate("/reports")}>Back to Reports</button></header>
      <section className="settlement-difference-cards" aria-label="Report matching summary">
        {summaryCards.map((card) => <button type="button" key={card.id} className={`settlement-summary-card ${card.tone} ${filters.status === card.id ? "active" : ""}`} onClick={() => updateFilter("status", filters.status === card.id ? "ALL" : card.id)}><span>{card.label}</span><strong>{card.value}</strong><small>{card.hint}</small></button>)}
      </section>
      <section className="settlement-difference-filters">
        <input placeholder="Order ID" value={filters.orderId} onChange={(event) => updateFilter("orderId", event.target.value)} />
        <input placeholder="Tracking ID" value={filters.trackingId} onChange={(event) => updateFilter("trackingId", event.target.value)} />
        <input placeholder="Seller Name" value={filters.seller} onChange={(event) => updateFilter("seller", event.target.value)} />
        <label className="settlement-date-filter">From<input type="date" value={filters.fromDate} onChange={(event) => updateFilter("fromDate", event.target.value)} /></label>
        <label className="settlement-date-filter">To<input type="date" value={filters.toDate} min={filters.fromDate} onChange={(event) => updateFilter("toDate", event.target.value)} /></label>
        <select value={filters.platform} onChange={(event) => updateFilter("platform", event.target.value)}><option value="">All Platforms</option>{platforms.map((platform) => <option key={platform} value={platform}>{platform}</option>)}</select>
        <select value={filters.status} onChange={(event) => updateFilter("status", event.target.value)}><option value="ALL">All Entries</option><option value="TRACKING_ENTRIES">1. Tracking Report</option><option value="T1_T2_MATCHED">1 to 2 Matched</option><option value="T1_T2_UNMATCHED">1 to 2 Unmatched</option><option value="ORDER_ENTRIES">2. Order Detail Report</option><option value="T2_T3_MATCHED">2 to 3 Matched</option><option value="T2_T3_UNMATCHED">2 to 3 Unmatched</option><option value="SETTLEMENT_ENTRIES">3. Settlement Report</option><option value="FULLY_MATCHED">Fully Matched</option></select>
        <button type="button" onClick={load}>Refresh</button>
      </section>
      <section className="settlement-difference-table"><div className="settlement-difference-count">{visible.length} records</div><div><table><thead><tr><th>Order ID</th><th>1. Tracking Entry</th><th>1 to 2 Match</th><th>2. Order Detail Entry</th><th>2 to 3 Match</th><th>3. Settlement Entry</th></tr></thead><tbody>
        {loading ? <tr><td colSpan="6">Loading...</td></tr> : visible.length === 0 ? <tr><td colSpan="6">No records found</td></tr> : visible.map((row) => {
          const trackingExists = present(row.tracking_entry_id); const orderExists = present(row.order_entry_id); const settlementExists = present(row.settlement_entry_id);
          return <tr key={row.order_id}><td><b>{row.order_id}</b></td><td>{trackingExists ? <>{row.tracking_id || "-"}<small>{row.seller_name || "-"}</small></> : "Unmatched"}</td><td><span className={trackingExists && orderExists ? "match" : "unmatch"}>{trackingExists && orderExists ? "Matched" : "Unmatched"}</span></td><td>{orderExists ? <>{row.sku || "-"}<small>{row.platform || "-"} / {row.order_item_status || "-"}</small></> : "Unmatched"}</td><td><span className={orderExists && settlementExists ? "match" : "unmatch"}>{orderExists && settlementExists ? "Matched" : "Unmatched"}</span></td><td>{settlementExists ? <>{`Rs. ${Number(row.bank_settlement || 0).toFixed(2)}`}<small>{[row.return_type, row.item_return_status].filter(Boolean).join(" / ") || "-"}</small></> : "Unmatched"}</td></tr>;
        })}
      </tbody></table></div></section>
    </main>
  );
}
